/**
 * #60 / DEC-365 — changing a job's pickup site: the contract the API, the phone and
 * web-dispatch share.
 *
 * A job's site is a tank battery / location (`jobs.hauler_location_id`, optionally with the
 * well on it the load came from, `jobs.source_well_id`) XOR a standalone well
 * (`jobs.pickup_well_id`). `SiteRef` names one side of a change in that shape.
 *
 * The office changes it with POST /v1/jobs/:id/site; the phone always queues a
 * `job_site_change` sync entity (`JobSiteChangeSyncPayload`), which the server applies only
 * when the job is still at `fromSite` (compare-and-set) and parks as a SITE_CHANGE
 * sync_conflicts row otherwise.
 *
 * Tanks: gauging data is ALWAYS kept on the ticket. When the change moves to another site, a
 * captured tank re-links to the new site's active tank with the same normalized name
 * (`normalizeTankName`) when exactly one matches, and is unlinked (name kept) otherwise.
 */

import { z } from 'zod';
import { toUpperIdentifier } from '../validation/identifier.js';

/** One side of a site change. A well pickup never has a source well. */
export type SiteRef =
  | { readonly kind: 'LOCATION'; readonly id: string; readonly sourceWellId: string | null }
  | { readonly kind: 'WELL'; readonly id: string; readonly sourceWellId: null };

export const siteRefSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('LOCATION'),
    id: z.string().uuid(),
    sourceWellId: z.string().uuid().nullable(),
  }),
  z.object({
    kind: z.literal('WELL'),
    id: z.string().uuid(),
    sourceWellId: z.null(),
  }),
]);

/** Why the site changed. A code is always required; OTHER also needs words. */
export const JOB_SITE_CHANGE_REASON_CODES = [
  'OTHER_WELL_ON_LOCATION',
  'WRONG_SITE_ASSIGNED',
  'OPERATOR_REQUEST',
  'OTHER',
] as const;
export type JobSiteChangeReasonCode = (typeof JOB_SITE_CHANGE_REASON_CODES)[number];

export const JOB_SITE_CHANGE_REASON_LABELS: Readonly<Record<JobSiteChangeReasonCode, string>> = {
  OTHER_WELL_ON_LOCATION: 'Load came from another well on this location',
  WRONG_SITE_ASSIGNED: 'Job was assigned to the wrong site',
  OPERATOR_REQUEST: 'Operator asked for a different site',
  OTHER: 'Other',
};

/** Minimum length of the reason text when the code is OTHER (SYNC_CONFLICT_REASON_MIN). */
export const JOB_SITE_CHANGE_REASON_OTHER_MIN = 10;
export const JOB_SITE_CHANGE_REASON_MAX = 500;

/** The reason pair, validated together: text is required (10+ characters) only for OTHER. */
const reasonFields = {
  reasonCode: z.enum(JOB_SITE_CHANGE_REASON_CODES),
  reason: z
    .string()
    .trim()
    .max(JOB_SITE_CHANGE_REASON_MAX)
    .nullable()
    .optional()
    .transform((v) => (v === undefined || v === null || v === '' ? null : v)),
};

function reasonRefinement(
  v: { reasonCode: JobSiteChangeReasonCode; reason: string | null },
  ctx: z.RefinementCtx,
): void {
  if (
    v.reasonCode === 'OTHER' &&
    (v.reason === null || v.reason.length < JOB_SITE_CHANGE_REASON_OTHER_MIN)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['reason'],
      message: `Say why, in at least ${String(JOB_SITE_CHANGE_REASON_OTHER_MIN)} characters.`,
    });
  }
}

/** The `payload` of a `job_site_change` sync entity (jobId and occurredAt ride the envelope). */
export const jobSiteChangeSyncPayloadSchema = z
  .object({ fromSite: siteRefSchema, toSite: siteRefSchema, ...reasonFields })
  .superRefine(reasonRefinement);
export type JobSiteChangeSyncPayload = z.input<typeof jobSiteChangeSyncPayloadSchema>;

/** Body of POST /v1/jobs/:id/site. `expectedSite` is the site the office was looking at. */
export const jobSiteChangeBodySchema = z
  .object({ toSite: siteRefSchema, expectedSite: siteRefSchema.optional(), ...reasonFields })
  .superRefine(reasonRefinement);
export type JobSiteChangeBody = z.input<typeof jobSiteChangeBodySchema>;

/** A captured tank moved to the new site's tank of the same name. */
export interface TankRelink {
  readonly tankName: string;
  readonly fromTankId: string;
  readonly toTankId: string;
}

/** What happened to the job's captured tank links. */
export interface SiteChangeTankOutcome {
  readonly tankRelinks: ReadonlyArray<TankRelink>;
  /** Tanks left without a link (no single same-name tank at the new site). Names as stored. */
  readonly unlinkedTankNames: ReadonlyArray<string>;
}

/** The job's site after a change, as POST /v1/jobs/:id/site returns it. */
export interface JobSiteChangeJob {
  readonly id: string;
  readonly tankBatteryId: string | null;
  readonly pickupWellId: string | null;
  readonly sourceWellId: string | null;
  readonly sourceWellName: string | null;
  readonly siteName: string | null;
}

/** 200 of POST /v1/jobs/:id/site. `changed: false` when the job was already at `toSite`. */
export interface JobSiteChangeResponse extends SiteChangeTankOutcome {
  readonly changed: boolean;
  readonly job: JobSiteChangeJob;
}

/** sync_conflicts.conflict_type of a parked site change. */
export const SITE_CHANGE_CONFLICT_KIND = 'SITE_CHANGE';

/**
 * The one tank-name comparison rule: trimmed, inner whitespace collapsed, upper-cased the
 * same way names are stored (DEC-147). Two tanks are "the same tank" across sites when this
 * is equal.
 */
export function normalizeTankName(name: string): string {
  return toUpperIdentifier(name.replace(/\s+/g, ' '));
}

/** True when the two refs name the same site (and the same source well). */
export function sameSiteRef(a: SiteRef, b: SiteRef): boolean {
  return a.kind === b.kind && a.id === b.id && a.sourceWellId === b.sourceWellId;
}

/** A well linked to a location, as a source-well choice. */
export interface SiteOptionWell {
  readonly wellId: string;
  readonly wellName: string | null;
  readonly apiNumber: string | null;
}

/** One site the job may move to (same customer, active, can be picked up from). */
export type SiteOption =
  | {
      readonly kind: 'LOCATION';
      readonly id: string;
      readonly name: string | null;
      readonly wells: ReadonlyArray<SiteOptionWell>;
    }
  | {
      readonly kind: 'WELL';
      readonly id: string;
      readonly name: string | null;
      readonly apiNumber: string | null;
    };

/**
 * GET /v1/jobs/:id/site-options (CHANGE_JOB_SITE) — everything the office's Change site and
 * tank re-pick controls need, readable by every role that may change the site (the reviewers
 * included, who cannot read the hauler catalog).
 */
export interface JobSiteOptionsResponse {
  readonly current: SiteRef | null;
  /** The job's site first, then the customer's other sites by name. Capped at `limit`. */
  readonly sites: ReadonlyArray<SiteOption>;
  /** True when more sites matched than were returned (narrow with ?q=). */
  readonly truncated: boolean;
  /** Active tanks at the job's current site, for the tank re-pick. */
  readonly tanks: ReadonlyArray<{ readonly id: string; readonly name: string }>;
  /**
   * The job's captured tank rows (every capture), with their current link. `haulerTankId`
   * null = not linked to a registered tank; `id` is what the re-pick route takes.
   */
  readonly captureTanks: ReadonlyArray<{
    readonly id: string;
    readonly tankName: string;
    readonly haulerTankId: string | null;
  }>;
}
